import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import apiClient from '../api/api';
import { FaBriefcase, FaGavel, FaStar, FaUserTie, FaTools, FaCheckCircle, FaClock, FaCheck, FaFlagCheckered } from 'react-icons/fa';
import { StatCard, SectionHeader, ActivityListItem } from '../components/DashboardComponents';

function WorkerDashboard() {
    const { user } = useAuth();
    const [recentBids, setRecentBids] = useState([]);
    const [runningJobs, setRunningJobs] = useState([]);
    const [stats, setStats] = useState({ averageRating: 'N/A', activeJobs: 0, reliability: 100 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchDashboardData = useCallback(async () => {
        if (!user?.profileId) return;
        
        setLoading(true);
        setError('');
        try {
            const [bidsResponse, jobsResponse, ratingsResponse, profileResponse] = await Promise.all([
                apiClient.get(`/bids/worker/${user.profileId}?limit=5`),
                apiClient.get(`/jobs/worker/${user.profileId}?status=running`),
                apiClient.get(`/profiles/${user.profileId}/ratings`),
                apiClient.get(`/profiles/${user.userId}`) // Fetch full profile for reliability
            ]);
            
            setRecentBids(bidsResponse.data || []);
            setRunningJobs(jobsResponse.data || []);
            
            const reliability = profileResponse.data?.worker_details?.reliability_score || 100;
            const completedCount = profileResponse.data?.worker_details?.completed_jobs_count || 0;

            if (ratingsResponse.data && ratingsResponse.data.length > 0) {
                const avg = ratingsResponse.data.reduce((acc, r) => acc + r.rating, 0) / ratingsResponse.data.length;
                setStats({ 
                    averageRating: avg.toFixed(1), 
                    activeJobs: jobsResponse.data?.length || 0,
                    reliability: reliability,
                    completed: completedCount
                });
            } else {
                setStats({ 
                    averageRating: 'N/A', 
                    activeJobs: jobsResponse.data?.length || 0,
                    reliability: reliability,
                    completed: completedCount
                });
            }
    
        } catch (err) {
            setError('Failed to load dashboard data. Some sections may be unavailable.');
            console.error("Dashboard fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [user?.profileId]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const formatCurrency = (amount) => `৳${(amount || 0).toLocaleString()}`;
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

    return (
        <div className="min-h-screen bg-slate-50 p-6 lg:p-10 font-sans">
            <div className="max-w-7xl mx-auto">
                
                {/* Header */}
                <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-black text-slate-800 tracking-tight">Dashboard</h1>
                        <p className="text-slate-500 mt-1">Welcome back, <span className="font-bold text-emerald-600">{user?.name}</span>!</p>
                    </div>
                    <div className="mt-4 md:mt-0 flex space-x-3">
                        <Link to={`/workers/profile/${user?.profileId}`} className="bg-white border border-slate-200 text-slate-600 px-5 py-2.5 rounded-xl font-bold hover:bg-slate-50 transition-all shadow-sm">
                            View Profile
                        </Link>
                        {/* Broker feature disabled for now */}
                        {/* {user?.role === 'worker' && (
                            <Link to="/become-a-broker" className="bg-slate-800 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-slate-900 transition-all shadow-lg shadow-slate-200">
                                Become a Broker
                            </Link>
                        )} */}
                    </div>
                </div>

                {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-xl mb-8 shadow-sm">
                        <p className="font-medium">Error loading dashboard</p>
                        <p className="text-sm">{error}</p>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    <StatCard 
                        title="Reliability" 
                        value={loading ? '...' : `${stats.reliability}%`} 
                        icon={FaCheckCircle} 
                        color="green" 
                    />
                    <StatCard 
                        title="Active Jobs" 
                        value={loading ? '...' : stats.activeJobs} 
                        icon={FaBriefcase} 
                        color="blue" 
                    />
                    <StatCard 
                        title="Avg. Rating" 
                        value={loading ? '...' : stats.averageRating} 
                        icon={FaStar} 
                        color="yellow" 
                    />
                    <StatCard 
                        title="Jobs Done" 
                        value={loading ? '...' : stats.completed} 
                        icon={FaFlagCheckered} 
                        color="purple" 
                    />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* Main Content: Activity & Jobs */}
                    <div className="lg:col-span-2 space-y-8">
                        
                        {/* Active Jobs Section - Grid Layout */}
                        <div>
                            <SectionHeader 
                                title="Active Projects" 
                                linkText="View All" 
                                linkTo="/worker-dashboard/running-jobs" 
                            />
                            
                            {loading ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
                                    {[1,2].map(i => <div key={i} className="h-32 bg-slate-100 rounded-2xl"></div>)}
                                </div>
                            ) : runningJobs.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {runningJobs.map(job => (
                                        <div key={job.id} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all group relative overflow-hidden">
                                            <div className="absolute top-0 left-0 w-full h-1 bg-blue-500"></div>
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 mb-1 block">In Progress</span>
                                                    <h4 className="font-bold text-slate-800 text-lg group-hover:text-blue-600 transition-colors line-clamp-1">{job.title}</h4>
                                                </div>
                                                <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs">
                                                    {job.poster?.name?.charAt(0) || 'E'}
                                                </div>
                                            </div>
                                            <div className="flex items-center text-xs text-slate-500 mb-4 space-x-3">
                                                <span className="flex items-center"><FaUserTie className="mr-1"/> {job.poster?.name}</span>
                                                <span className="flex items-center"><FaClock className="mr-1"/> {formatDate(job.endDate)}</span>
                                            </div>
                                            <Link to={`/job/${job.id}`} className="block w-full py-2 bg-slate-50 text-slate-600 font-bold text-center rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm">
                                                View Details
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 bg-white rounded-3xl border border-dashed border-slate-200">
                                    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
                                        <FaCheckCircle className="text-2xl" />
                                    </div>
                                    <p className="text-slate-500 font-medium">No active jobs right now.</p>
                                    <Link to="/jobs" className="text-emerald-600 font-bold text-sm mt-2 inline-block hover:underline">Find Work</Link>
                                </div>
                            )}
                        </div>

                        {/* Recent Bids Section - Feed Layout */}
                        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                            <SectionHeader title="Recent Bids" />
                            {loading ? (
                                <div className="space-y-4 animate-pulse">
                                    {[1,2].map(i => <div key={i} className="h-16 bg-slate-50 rounded-xl"></div>)}
                                </div>
                            ) : recentBids.length > 0 ? (
                                <div className="space-y-0 divide-y divide-slate-50">
                                    {recentBids.map(bid => (
                                        <div key={bid.id} className="py-4 flex items-center justify-between group hover:bg-slate-50 rounded-lg px-2 -mx-2 transition-colors">
                                            <div className="flex items-center space-x-4">
                                                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center font-bold text-lg shadow-sm">
                                                    ৳
                                                </div>
                                                <div>
                                                    <h4 className="font-bold text-slate-800 text-sm group-hover:text-purple-700 transition-colors">{bid.job?.title || 'Unknown Job'}</h4>
                                                    <p className="text-xs text-slate-400">{formatDate(bid.createdAt)}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <span className="block font-bold text-slate-700 text-sm">{formatCurrency(bid.bidAmount)}</span>
                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${bid.status === 'accepted' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                                                    {bid.status || 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-slate-400 italic text-sm">
                                    You haven't placed any bids lately.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar: Tools Only (Earnings removed) */}
                    <div className="lg:col-span-1 space-y-8">
                        {/* Tools / Instruments */}
                        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                            <SectionHeader title="My Equipment" linkText="Manage" linkTo="/worker-dashboard/instruments" />
                            {user?.instruments && user.instruments.length > 0 ? (
                                <ul className="space-y-3">
                                    {user.instruments.slice(0, 4).map(inst => (
                                        <li key={inst.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-xl">
                                            <div className="flex items-center space-x-3">
                                                <div className="bg-white p-2 rounded-lg text-slate-400 shadow-sm"><FaTools size={12}/></div>
                                                <span className="font-bold text-slate-700 text-sm">{inst.name}</span>
                                            </div>
                                            <span className="text-xs font-bold bg-slate-200 text-slate-600 px-2 py-1 rounded-md">x{inst.Worker_Instruments?.quantity || 1}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-slate-400 text-sm text-center italic">No equipment listed.</p>
                            )}
                            <button className="w-full mt-4 border border-dashed border-slate-300 text-slate-500 py-3 rounded-xl font-bold hover:bg-slate-50 hover:text-slate-700 transition-all flex items-center justify-center">
                                + Add Tool
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WorkerDashboard;
